/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
 * and Foreign Patents, patent applications, and are protected by trade secret
 * or copyright law. Dissemination of this information, reproduction of this
 * material, and copying or distribution of this software is strictly forbidden
 * unless prior written permission is obtained from Pearson Education, Inc.
 */

/* eslint-disable no-param-reassign */
import { types } from 'mobx-state-tree';
import { toJS } from 'mobx';
import User from './User';
import { getUsers as getUsersService } from '../services/getUsers';
import { removeUser as removeUserService } from '../services/removeUser';
import { addUser as addUserService } from '../services/addUser';
import {
  sortByPropAndDirection,
  getProjectCardBackgroundColor
} from '../utils';
import constants from '../constants';
import { UserRole } from './UserRoles';

export default types
  .model('UserManagement', {
    visible: false,
    users: types.array(User),
    admins: types.array(User),
    // state to be updated when members are searched
    filteredUser: types.array(User),
    // state to be updated when members are searched
    filteredAdmins: types.array(User),
    searchTerm: types.frozen(''),
    columnToSort: 'lastModifiedDate',
    sortDirection: constants.USER_MANAGEMENT_DEFAULT_SORT_DIRECTION,
    userRoles: types.map(UserRole),
    selectedUsersForRemoval: types.array(types.string),
    selectAllUserForRemoval: false,
    isLegacyUserManagement: false
  })
  .actions(self => ({
    openUserManagementDrawer() {
      self.visible = true;
    },
    closeUserManagementDrawer() {
      self.visible = false;
    },
    async getUsersByEntity(entityUrn, instituteID) {
      self.users = [];
      self.admins = [];
      const response = await getUsersService(entityUrn);
      if (response && typeof response.isLegacyUserManagement === 'boolean') {
        self.setIsLegacyUserManagement(
          response.isLegacyUserManagement === true
        );
      }
      if (response.users && response.users.length > 0) {
        self.setUsers(response, instituteID);
        self.sortMembers();
      }
    },
    setUsers(data, instituteID) {
      self.users = [];
      self.admins = [];
      const { users } = data;

      // pushing users into Model.users
      users.forEach(user => {
        const { backgroundColor: color } = getProjectCardBackgroundColor();
        self.users.push({ ...user, color });
      });

      // pushing admins into Model.admins
      // admins.forEach(admin => {
      //   const { backgroundColor: color } = getProjectCardBackgroundColor();
      //   self.admins.push({ ...admin, roleId: 'admin', color });
      // });

      self.updateRoleLabel(instituteID);
    },
    updateUsers(userIds) {
      // eslint-disable-next-line no-unused-expressions
      userIds &&
        userIds.forEach(userId => {
          const index = self.users.findIndex(user => user.userId === userId);
          if (index !== -1) {
            self.users.splice(index, 1);
          }
        });
      // if SearchTerm exists update the filter users array too
      if (self.searchTerm) {
        self.searchUser(self.searchTerm);
      }
    },
    addNewUserToLocalData(newUser) {
      const allUsers = [...toJS(self.users), newUser];
      self.users = [];

      // pushing users into Model.users
      allUsers.forEach(user => {
        const { backgroundColor: color } = getProjectCardBackgroundColor();
        self.users.push({ ...user, color });
      });

      self.updateRoleLabel();
      self.sortMembers();

      // if SearchTerm exists update the filter users array too
      if (self.searchTerm) {
        self.searchUser(self.searchTerm);
      }
    },
    async removeUserByEntityAndId(entityUrn, userIds, instituteID) {
      // Resolve pearsonUIDs from selected userIds for the remove API
      const pearsonUIDs = userIds
        .map(id => self.users.find(u => u.userId === id)?.pearsonUID)
        .filter(Boolean);
      await removeUserService(entityUrn, pearsonUIDs, instituteID);
      self.updateUsers(userIds);
      // after removal of user reset the checked state of all users to false
      self.selectUnselectAllUsersForRemoval(false);
    },
    async addUser(
      entityUrn,
      roleId,
      userId,
      email,
      instituteID,
      pearsonUID,
      legacy
    ) {
      await addUserService(entityUrn, roleId, userId, instituteID, legacy);
      self.addNewUserToLocalData({
        userId,
        roleId,
        pearsonUID,
        roleLabel: self.userRoles.get(roleId)?.label || roleId,
        email,
        lastModifiedDate: new Date().toISOString()
      });
    },

    // add bulk user
    addBulkUsers(users) {
      users.forEach(user => {
        const { userId, roleId, email, lastModifiedDate } = user;
        self.addNewUserToLocalData({ userId, roleId, email, lastModifiedDate });
      });
      // after addition of users reset the checked state of all users to false
      self.selectUnselectAllUsersForRemoval(false);
    },

    setSearchTerm(searchTerm) {
      self.searchTerm = searchTerm;
    },
    resetSearchUser(loggedInUser) {
      self.searchTerm = '';
      self.filteredUser = [];
      self.filteredAdmins = [];
      self.validateUserSelectionCount(loggedInUser);
    },
    searchUser(searchTerm, loggedInUser) {
      const allUsers = [...toJS(self.users)];
      const allAdmins = [...toJS(self.admins)];
      self.filteredUser = [];
      self.filteredAdmins = [];
      if (searchTerm) {
        allUsers.forEach(user => {
          if (
            user.userId.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase())
          ) {
            self.filteredUser.push(user);
          }
        });
        allAdmins.forEach(user => {
          if (
            user.userId.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase())
          ) {
            self.filteredAdmins.push(user);
          }
        });
        self.validateUserSelectionCount(loggedInUser);
      }
    },
    sortUsers() {
      const allUsers = [...toJS(self.users)];

      // sort users and admins
      allUsers.sort(
        sortByPropAndDirection(self.columnToSort, self.sortDirection)
      );

      self.users = [];

      // pushing users into Model.users
      allUsers.forEach(user => {
        self.users.push(user);
      });

      // Sorting filtered Users
      if (self.searchTerm) {
        const allFilteredUsers = [...toJS(self.filteredUser)];
        allFilteredUsers.sort(
          sortByPropAndDirection(self.columnToSort, self.sortDirection)
        );

        self.filteredUser = [];
        allFilteredUsers.forEach(user => {
          self.filteredUser.push(user);
        });
      }
    },
    sortAdmins() {
      const allAdmins = [...toJS(self.admins)];

      allAdmins.sort(
        sortByPropAndDirection(self.columnToSort, self.sortDirection)
      );

      self.admins = [];

      // pushing admins into Model.admins
      allAdmins.forEach(admin => {
        self.admins.push({ ...admin, roleId: 'admin' });
      });

      // Sorting filtered admins
      if (self.searchTerm) {
        const allFilteredAdmins = [...toJS(self.filteredAdmins)];
        allFilteredAdmins.sort(
          sortByPropAndDirection(self.columnToSort, self.sortDirection)
        );

        self.filteredAdmins = [];
        allFilteredAdmins.forEach(user => {
          self.filteredAdmins.push(user);
        });
      }
    },
    sortMembers() {
      self.sortUsers();
      self.sortAdmins();
    },
    setSortDirection(value) {
      self.sortDirection = value;
    },
    setColumnToSort(value) {
      self.columnToSort = value;
    },
    resetSortCriteria() {
      self.columnToSort = 'lastModifiedDate';
      self.sortDirection = constants.USER_MANAGEMENT_DEFAULT_SORT_DIRECTION;
    },
    addRoleLabel(roles, instituteID) {
      const objUserRoles = Object.fromEntries(toJS(roles));
      self.userRoles = objUserRoles;
      self.updateRoleLabel(instituteID);
    },
    updateUsersRoleLabel(instituteID) {
      if (self.userRoles.size > 0) {
        const allUsers = [...toJS(self.users)];
        self.users = [];

        // adding roles label to users
        allUsers.forEach(user => {
          let roleLabel = '';
          user.roleLabel.split(',').forEach(role => {
            let label = '';
            if (self.userRoles.get(role + '_' + instituteID)) {
              label = self.userRoles.get(role + '_' + instituteID).label;
            } else if (self.userRoles.get(role)) {
              label = self.userRoles.get(role).label;
            } else {
              label = role;
            }
            roleLabel += (roleLabel ? ', ' : '') + label;
          });

          self.users.push({ ...user, roleLabel });
        });

        // update user roles in filtered array
        if (self.searchTerm) {
          const allFilteredUsers = [...toJS(self.filteredUser)];
          self.filteredUser = [];

          // adding roles label to users
          allFilteredUsers.forEach(user => {
            let roleLabel = '';

            user.roleLabel.split(',').forEach(role => {
              roleLabel +=
                (roleLabel ? ', ' : '') +
                (self.userRoles.get(role)
                  ? self.userRoles.get(role).label
                  : role);
            });

            self.filteredUser.push({ ...user, roleLabel });
          });
        }
      }
    },
    updateAdminsRoleLabel() {
      if (self.userRoles) {
        const allAdmins = [...toJS(self.admins)];

        self.admins = [];
        // adding roles label to admins
        allAdmins.forEach(admin => {
          const roleLabel = self.userRoles.get(admin.roleId)
            ? self.userRoles.get(admin.roleId).label
            : '';

          self.admins.push({ ...admin, roleLabel });
        });
      }
    },
    updateRoleLabel(instituteID) {
      self.updateUsersRoleLabel(instituteID);
      self.updateAdminsRoleLabel();
    },
    updateUserData(userId, newRoleId, lastModifiedDate) {
      const [updatedUser] = self.users.filter(user => user.userId === userId);
      updatedUser.updateUserRole(newRoleId);
      updatedUser.updateLastModifiedDate(lastModifiedDate);

      if (self.searchTerm) {
        const [updatedFilteredUser] = self.filteredUser.filter(
          user => user.userId === userId
        );
        updatedFilteredUser.updateUserRole(newRoleId);
        updatedFilteredUser.updateLastModifiedDate(lastModifiedDate);
      }

      self.updateRoleLabel();
      self.sortMembers();
    },
    updateUserRoles(roleId, label, owner, createdBy) {
      self.userRoles.put({ roleId, label, owner, createdBy });
    },
    // updating checkbox state for a user Id
    selectUnselectUsersForRemoval(userId, loggedInUser, allUnAuthorizedUsers) {
      self.selectAllUserForRemoval = false;
      const index = self.selectedUsersForRemoval.findIndex(
        user => user === userId
      );

      const loggedIUuser = self.users.find(
        user => user.userId === loggedInUser
      );

      // id user is already added to selectedUsers array remove it else add it to array
      if (index !== -1) {
        self.selectedUsersForRemoval.splice(index, 1);
      } else {
        self.selectedUsersForRemoval.push(userId);
      }
      // will update the value of select All checkbox
      self.validateUserSelectionCount(loggedIUuser, allUnAuthorizedUsers);
    },

    validateUserSelectionCount(loggedInUser, allUnAuthorizedUsers) {
      // Calculate total selectable users (exclude admin users, logged in user, and unauthorized users)
      const adminUsers = self.users.filter(
        user => user.roleLabel === constants.ROLE_ADMINISTRATOR
      );
      const allUsers =
        allUnAuthorizedUsers && allUnAuthorizedUsers.length > 0
          ? self.users.length - allUnAuthorizedUsers.length - adminUsers.length
          : self.users.length - adminUsers.length;

      // Subtract logged in user if they exist
      const selectableUsers = loggedInUser ? allUsers - 1 : allUsers;

      // if the length of selected users array equals the number of selectable users, check select all
      if (
        self.selectedUsersForRemoval.length === selectableUsers &&
        selectableUsers > 0
      ) {
        self.selectAllUserForRemoval = true;
      } else {
        self.selectAllUserForRemoval = false;
      }
    },
    // updating the checkbox state for all users
    selectUnselectAllUsersForRemoval(value, loggedInUser) {
      self.selectedUsersForRemoval = [];
      if (value === false) {
        self.selectAllUserForRemoval = false;
      } else {
        // if search term is selected use filtered user array else use default user array
        const userArray = self.searchTerm
          ? [...toJS(self.filteredUser)]
          : [...toJS(self.users)];
        userArray.forEach(user => {
          // exclude logged in user, admin users, and unauthorized users from bulk selection
          if (
            user.userId !== loggedInUser &&
            user.roleLabel !== constants.ROLE_ADMINISTRATOR &&
            user.isAuthorizedUser
          ) {
            self.selectedUsersForRemoval.push(user.userId);
            self.selectAllUserForRemoval = true;
          }
        });
      }
    },
    setIsLegacyUserManagement(value) {
      self.isLegacyUserManagement = value;
    }
  }));
